import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { View } from 'react-native';
import type { Loan } from 'app/types/loan';
import React, { useEffect } from 'react';
import { Modal } from 'app/design/primitives/modal';
import { useTranslation } from 'react-i18next';
import { useCreateListing } from 'app/network/listing';
import { useOneChannel } from 'app/network/chat/channel';
import { useAllAddresses } from 'app/network/address';
import { useAllGroups } from 'app/network/groups';
import { useNavigation } from 'app/hooks/navigation';
import { useAtom } from 'jotai';
import { presentCreateOfferModalAtom } from 'app/atoms';
import { ListingSubtype, ListingType } from 'app/types/listing';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCircleCheck } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export interface CreateOfferFromRequestModalProps {
  loan: Loan;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useCreateOfferFromRequestModal(loan: Loan): CreateOfferFromRequestModalProps {
  const [isOpen, setIsOpen] = useAtom(presentCreateOfferModalAtom);
  return {
    loan,
    isOpen,
    setIsOpen,
  };
}

export function CreateOfferFromRequestModal({ loan, isOpen, setIsOpen }: CreateOfferFromRequestModalProps) {
  const { t } = useTranslation('one-listing');
  const navigation = useNavigation();

  const { data: addresses } = useAllAddresses();
  const { data: groups } = useAllGroups(true);

  const { mutate, isPending: isLoading } = useCreateListing();
  const isRequest = loan.listing.type === ListingType.request;
  const linkedOfferQuery = useOneChannel(isRequest ? loan.channelURL : undefined);
  const hasLinkedOffer = Boolean(linkedOfferQuery.data?.linkedOffer);

  useEffect(() => {
    if (hasLinkedOffer && isOpen) {
      setIsOpen(false);
    }
  }, [hasLinkedOffer, isOpen, setIsOpen]);

  const onConfirmCreationReturnPress = () => {
    mutate(
      {
        type: ListingType.offer,
        subtype: ListingSubtype.object,
        categoryPrimaryId: loan.listing.categoryPrimary.id,
        serviceCategoryId: loan.listing.serviceCategory?.id ?? null,
        item: {
          name: loan.listing.item.name,
        },
        groupIds: groups?.map((g) => g.id) ?? [],
        addressIds: addresses?.map((a) => a.id) ?? [],
        description: null,
        start: null,
        end: null,
        picture: null,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          navigation.navigate('quickNew', { screen: 'quick-new-thanks' }, { pop: true });
        },
      },
    );
  };

  // A linked offer already fulfils this request. Wait for the channel state so
  // the modal never flashes before we know whether an offer is linked.
  if (isRequest && (!linkedOfferQuery.isSuccess || linkedOfferQuery.isFetching || hasLinkedOffer)) {
    return null;
  }

  return (
    <Modal onClose={() => setIsOpen(false)} open={isOpen} transparent>
      <Modal.Card>
        <Row justifyContent="center" paddingTop="spacing.general.x4">
          <IconCircleCheck strokeWidth={ICON_STROKE_WIDTH} size={30} color={theme.colors['text.positive']} />
        </Row>
        <Modal.Title>{t('createOfferModal.title', { ns: 'one-listing' })}</Modal.Title>

        <Column>
          <Text textAlign="center">{t('createOfferModal.text', { ns: 'one-listing' })}</Text>
        </Column>

        <View>
          <Button
            loading={isLoading}
            marginBottom="spacing.general.x4"
            onPress={onConfirmCreationReturnPress}
            variant="primary"
          >
            {t('createOfferModal.primaryButtonText', { ns: 'one-listing' })}
          </Button>

          <Button onPress={() => setIsOpen(false)} variant="secondary">
            {t('createOfferModal.secondaryButtonText', {
              ns: 'one-listing',
            })}
          </Button>
        </View>
      </Modal.Card>
    </Modal>
  );
}
